import { useEffect, useRef } from 'react';

import { cn } from '@/lib/cn';

interface Props {
  /** MP4 URL produced by the video2d provider (opaque). */
  src: string;
  /** Poster image while the video buffers or pre-play. */
  poster?: string;
  /** Hex color to key out, e.g. "#FF00FF". Defaults to magenta. */
  keyColor?: string;
  /**
   * Hard-key radius. Pixels within this RGB distance of keyColor get
   * alpha = 0 (fully transparent). Default 60.
   */
  hardRadius?: number;
  /**
   * Soft-key radius. Pixels between hardRadius and softRadius get a graduated
   * alpha (linear ramp 0 → 255). This eliminates the binary "halo" of the
   * naïve keyer. Must be > hardRadius. Default 120.
   */
  softRadius?: number;
  /**
   * Despill strength (0-1). When > 0, pixels that survive the key but carry
   * a magenta tint (typical "color spill" from the chroma background on
   * the subject) get their R and B channels pulled toward G to neutralize
   * the cast. Set 0 to disable. Default 0.5 — strong enough to clean rim
   * light, gentle enough to keep subject colors saturated.
   */
  despill?: number;
  /**
   * Spill detection radius. Despill only fires on pixels within this RGB
   * distance of keyColor — the zone where actual color cast lives.
   * Pixels far from the key keep their original saturation. Default 200.
   */
  despillRadius?: number;
  /**
   * How much excess in off-anchor channels triggers despill (in 0-255
   * units). Higher = only obvious spill gets corrected. Default 30
   * (was 10 in the first version, which clobbered any pixel that had
   * R or B even slightly above G — that's why saturation dropped).
   */
  despillThreshold?: number;
  /** Tailwind / inline classes for the wrapping canvas. */
  className?: string;
  /** Optional pause when offscreen / on tab hide (we don't auto-do that). */
  paused?: boolean;
}

/**
 * Phase 7c — renders an opaque MP4 with a per-frame canvas chroma-key pass
 * so the chosen background color (default magenta) becomes transparent.
 *
 * Why canvas instead of WebGL: a canvas 2d shader is ~10 lines, fits inside
 * a single component, and on a 768×1024 6-second clip at 24 fps runs at
 * comfortably real-time on the M-class iPhone / M1 Android baseline (~15 ms
 * per frame budget). For higher resolutions or longer clips, swap to
 * WebGL — left as a follow-up if anyone hits perf issues.
 *
 * The video element is offscreen (hidden) — only the canvas is shown.
 */
export function ChromaKeyVideo({
  src,
  poster,
  keyColor = '#FF00FF',
  hardRadius = 60,
  softRadius = 120,
  despill = 0.5,
  despillRadius = 200,
  despillThreshold = 30,
  className,
  paused = false,
}: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    const target = hexToRgb(keyColor);
    // Squared radii — avoids per-pixel sqrt(). The keyer thinks in
    // squared distance throughout.
    const hardR2 = hardRadius * hardRadius;
    const softR2 = softRadius * softRadius;
    const despillR2 = despillRadius * despillRadius;
    // Detect "magenta-like" keys: when target has high R+B and low G, color
    // spill on the subject shows up as pixels whose R and B exceed G by a
    // meaningful margin. We use a single channel as the "anti-spill anchor"
    // — for magenta (255,0,255) that's G; for green (0,255,0) it'd be R+B,
    // etc. This generalizes to any hue.
    const anchorChan = pickAnchorChannel(target);
    let rafId = 0;
    let frameW = 0;
    let frameH = 0;

    const draw = () => {
      if (video.readyState >= 2 && !video.paused && !video.ended) {
        if (frameW !== video.videoWidth || frameH !== video.videoHeight) {
          frameW = video.videoWidth;
          frameH = video.videoHeight;
          canvas.width = frameW;
          canvas.height = frameH;
        }
        ctx.drawImage(video, 0, 0, frameW, frameH);
        const frame = ctx.getImageData(0, 0, frameW, frameH);
        const data = frame.data;
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const dr = r - target.r;
          const dg = g - target.g;
          const db = b - target.b;
          const d2 = dr * dr + dg * dg + db * db;

          if (d2 < hardR2) {
            // Inside the hard radius → fully keyed.
            data[i + 3] = 0;
            continue;
          }
          if (d2 < softR2) {
            // Soft edge → graduated alpha. Linear ramp in distance
            // (perceptually close enough; eliminates the visible halo).
            const t = (Math.sqrt(d2) - hardRadius) / (softRadius - hardRadius);
            data[i + 3] = Math.round(t * 255);
            // Despill kicks in for soft-edge pixels too — that's where
            // color cast is strongest.
          }

          // Despill: only fire when the pixel is reasonably close to the
          // key color (real color-spill zone) AND when the off-anchor
          // channels show a meaningful excess. This avoids desaturating
          // pixels that just happen to have warm tones.
          if (despill > 0 && d2 < despillR2) {
            if (anchorChan === 'G') {
              // Magenta family: kill R and B excess over G.
              const cap = g + despillThreshold;
              if (r > cap) data[i] = Math.round(r - (r - cap) * despill);
              if (b > cap) data[i + 2] = Math.round(b - (b - cap) * despill);
            } else if (anchorChan === 'R') {
              // Cyan family: kill G and B excess over R.
              const cap = r + despillThreshold;
              if (g > cap) data[i + 1] = Math.round(g - (g - cap) * despill);
              if (b > cap) data[i + 2] = Math.round(b - (b - cap) * despill);
            } else {
              // Green family: kill G excess over max(R,B).
              const cap = Math.max(r, b) + despillThreshold;
              if (g > cap) data[i + 1] = Math.round(g - (g - cap) * despill);
            }
          }
        }
        ctx.putImageData(frame, 0, 0);
      }
      rafId = requestAnimationFrame(draw);
    };

    rafId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafId);
  }, [keyColor, hardRadius, softRadius, despill, despillRadius, despillThreshold]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (paused) v.pause();
    else void v.play().catch(() => undefined);
  }, [paused]);

  return (
    <>
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        autoPlay
        loop
        muted
        playsInline
        crossOrigin="anonymous"
        className="hidden"
      />
      <canvas
        ref={canvasRef}
        className={cn('h-full w-full object-contain', className)}
      />
    </>
  );
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '');
  const v = h.length === 3
    ? h.split('').map(c => c + c).join('')
    : h;
  const n = parseInt(v, 16);
  return {
    r: (n >> 16) & 0xff,
    g: (n >> 8) & 0xff,
    b: n & 0xff,
  };
}

/**
 * Returns the channel that should be the "anti-spill anchor" for a given
 * key color. The anchor channel is the one that's LOWEST in the key —
 * spill on the subject shows up as the other two channels rising above it.
 * Despill then clamps those channels back down toward the anchor.
 */
function pickAnchorChannel({ r, g, b }: { r: number; g: number; b: number }): 'R' | 'G' | 'B' {
  const min = Math.min(r, g, b);
  if (min === r) return 'R';
  if (min === g) return 'G';
  return 'B';
}
